import type { DialogRoot } from '@base-ui/react/dialog';
import type { OverlayTriggerProps, OverlayTriggerState } from 'react-stately';
import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { merge } from 'lodash-es';
import React from 'react';
import { useOverlayTriggerState } from 'react-stately';

import type { HeightTypes } from '../../utilities/shared/Height';
import type { MaxHeightTypes } from '../../utilities/shared/MaxHeight';
import type { MaxWidthTypes } from '../../utilities/shared/MaxWidth';
import type { MinHeightTypes } from '../../utilities/shared/MinHeight';
import type { MinWidthTypes } from '../../utilities/shared/MinWidth';
import type { WidthTypes } from '../../utilities/shared/Width';
import type { ShellProps } from '../Shell/Shell';
import type { ShellContentProps } from '../Shell/ShellContent';
import type { ShellFooterProps } from '../Shell/ShellFooter';
import type { ShellHeaderProps } from '../Shell/ShellHeader';
import { fade, palette } from '../../common/colors';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { heightCSS } from '../../utilities/shared/Height';
import { maxHeightCSS } from '../../utilities/shared/MaxHeight';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minHeightCSS } from '../../utilities/shared/MinHeight';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { ClearAccessLevelContext } from '../../utilities/useAccessLevelContext';
import { Shell } from '../Shell/Shell';
import { ShellContent } from '../Shell/ShellContent';
import { ShellFooter } from '../Shell/ShellFooter';
import { ShellHeader } from '../Shell/ShellHeader';

export type { OverlayTriggerState };

/**
 * `OverlayTriggerState` extended with an exit-animation hook. URL-mounted dialogs close in two
 * steps: `close()` flips the state so the exit animation plays, then `onCloseComplete` fires once
 * it finishes so the caller can navigate away (which unmounts the dialog route).
 */
export type DialogTriggerState = OverlayTriggerState & {
  onCloseComplete?: () => void;
};

const DialogControlContext = React.createContext<null | { onClose: () => void }>(null);

// A hook for creating controlled dialogs
export function useDialogState(props?: OverlayTriggerProps) {
  const state = useOverlayTriggerState(props ?? {});

  // In menus, the menu component will return focus to the trigger when it closes, which cancels
  // out the intended focus behavior when a Dialog opens. To prevent clobbering the focus
  // behavior, we wait until the next tick to open the dialog.
  const openFromMenu = React.useCallback(
    (event?: Event | React.BaseSyntheticEvent, delay = 50) => {
      setTimeout(state.open, delay);
    },
    [state],
  );

  return { state, openFromMenu };
}

export function useDialogStateCallback(props?: OverlayTriggerProps) {
  const { state, openFromMenu: baseOpenFromMenu } = useDialogState(props);
  const openFromMenu = React.useCallback(() => baseOpenFromMenu(), [baseOpenFromMenu]);
  return {
    state,
    openFromMenu,
  };
}

// Enter is a keyframe animation on the persistent styles: Base UI skips its
// `[data-starting-style]` pass when a dialog mounts already open (route dialogs do), but the
// popup DOM mounts fresh on every open, so a mount animation always plays. Exit stays
// transition-based via `[data-ending-style]` so Base UI delays unmount for it.
const ENTER_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const EXIT_EASING = 'ease-in';
const TRANSITION_DURATION = '250ms';

/** The popup's "from" state on enter and "to" state on exit — both ends are the same. */
const POPUP_HIDDEN = { opacity: 0, transform: 'translateY(-2%) scale(0.96)' };

const overlayShow = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const contentShow = keyframes({
  from: POPUP_HIDDEN,
  to: { opacity: 1, transform: 'translateY(0) scale(1)' },
});

export const ModalOverlay = styled(BaseDialog.Backdrop, {
  position: 'fixed',
  inset: 0,
  zIndex: zIndexes.dialogWash,
  backgroundColor: fade(palette.gray900, 0.1),
  backdropFilter: 'blur(5px)',
  boxShadow: `inset 0px 80px 240px 240px ${fade(palette.gray900, 0.12)}`,

  [darkThemeSelector]: {
    backgroundColor: fade(palette.gray900, 0.3),
  },

  variants: {
    internal: {
      true: {
        backgroundColor: fade(palette.internalBgLight, 1),
        boxShadow: `inset 0px 80px 240px 240px ${fade(palette.internalHeadingLight, 0.12)}`,

        [darkThemeSelector]: {
          backgroundColor: fade(palette.internalBgDark, 1),
          boxShadow: `inset 0px 80px 240px 240px ${fade(palette.internalHeadingDark, 0.12)}`,
        },
      },
    },
    suppressAnimations: {
      true: {},
      false: {
        '@media (prefers-reduced-motion: no-preference)': {
          animation: `${overlayShow} ${TRANSITION_DURATION} ${ENTER_EASING}`,
          transition: `opacity ${TRANSITION_DURATION} ${ENTER_EASING}`,

          '&[data-ending-style]': {
            animation: 'none',
            opacity: 0,
            transitionTimingFunction: EXIT_EASING,
          },
        },
      },
    },
  },
});
ModalOverlay.displayName = 'ModalOverlay';

const ModalViewport = styled(BaseDialog.Viewport, {
  position: 'fixed',
  inset: 0,
  zIndex: zIndexes.dialogContent,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});
ModalViewport.displayName = 'ModalViewport';

const Modal = styled(BaseDialog.Popup, {
  willChange: 'transform, opacity',
  pointerEvents: 'auto',
  display: 'flex',
  outline: 'none',

  variants: {
    fullscreen: {
      true: {
        width: '100%',
        height: '100%',
      },
      false: {
        '@mobile': {
          width: '90vw',
          minWidth: '$300 !important',
          maxWidth: '90vw !important',
          maxHeight: '90vh !important',
        },

        '@notMobile': {
          width: '90vw',
          maxWidth: '$660',
          maxHeight: '85vh',
        },
      },
    },
    suppressAnimations: {
      true: {},
      false: {
        '@media (prefers-reduced-motion: no-preference)': {
          animation: `${contentShow} ${TRANSITION_DURATION} ${ENTER_EASING}`,
          transition: `opacity ${TRANSITION_DURATION} ${ENTER_EASING}, transform ${TRANSITION_DURATION} ${ENTER_EASING}`,

          '&[data-ending-style]': {
            ...POPUP_HIDDEN,
            animation: 'none',
            transitionTimingFunction: EXIT_EASING,
          },
        },
      },
    },
  },
});
Modal.displayName = 'Modal';

const DialogHeaderContainer = styled(ShellHeader);
DialogHeaderContainer.displayName = 'DialogHeaderContainer';

export type DialogHeaderProps = ShellHeaderProps & {
  onClose?: () => void;
};

export function DialogHeader({ onClose, ...remaining }: DialogHeaderProps) {
  const control = React.useContext(DialogControlContext);

  return (
    <DialogHeaderContainer
      onClose={onClose ?? control?.onClose}
      closeButtonAriaLabel="Close dialog"
      type="dialog"
      {...remaining}
    />
  );
}

const DialogContentContainer = styled(ShellContent);
DialogContentContainer.displayName = 'DialogContentContainer';

export type DialogContentProps = ShellContentProps & {
  width?: string;
  maxWidth?: string;
  height?: string;
  maxHeight?: string;
};

export function DialogContent({
  children,
  width,
  maxWidth,
  height,
  maxHeight,
  ...remaining
}: DialogContentProps) {
  return (
    <DialogContentContainer
      {...remaining}
      type="dialog"
      style={{ width, maxWidth, height, maxHeight }}
    >
      {children}
    </DialogContentContainer>
  );
}

const DialogFooterContainer = styled(ShellFooter);
DialogFooterContainer.displayName = 'DialogFooterContainer';

export type DialogFooterProps = {
  /**
   * Provide any actions for your drawer.
   */
  actions?: React.ReactNode;
};

export function DialogFooter({ actions, ...remaining }: DialogFooterProps & ShellFooterProps) {
  return <DialogFooterContainer end={actions} {...remaining} type="dialog" />;
}

const DialogShellContainer = styled(Shell, {
  height: 'auto',
  padding: '$8 0',
  overflow: 'hidden',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.modalLight,
  borderRadius: '$12',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.modalDark,
  },

  variants: {
    fullscreen: {
      true: {
        padding: 0,
        borderRadius: 0,
      },
      false: {},
    },
  },
});
DialogShellContainer.displayName = 'DialogShellContainer';

type DialogShellProps = ShellProps & {
  children: React.ReactNode;
  fullscreen: boolean;
};

function DialogShell({ children, contentMode, fullscreen, ...remaining }: DialogShellProps) {
  return (
    <DialogShellContainer contentMode={contentMode} fullscreen={fullscreen} {...remaining}>
      {children}
    </DialogShellContainer>
  );
}

export type DialogPreset = 'narrow' | 'intermediate' | 'wide';
export type DialogVerticalAnchor = 'center' | 'topPinned';

const DIALOG_PRESET_KEYS: Record<DialogPreset, true> = {
  narrow: true,
  intermediate: true,
  wide: true,
};

/** All `DialogPreset` values, aligned with the max-width mapping in `Dialog`. */
export const DIALOG_PRESET_VALUES = Object.keys(DIALOG_PRESET_KEYS) as DialogPreset[];

type DialogDivPassthrough = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  keyof ShellProps | 'children'
>;

export type DialogProps = DialogDivPassthrough &
  Omit<ShellProps, 'type' | 'width' | 'maxWidth' | 'height' | 'maxHeight'> & {
    /**
     * Select a width preset.
     */
    preset?: DialogPreset;
    /**
     * Anchor the dialog vertically. `center` (default) recenters as content
     * height changes; `top` pins the top edge so dynamic content doesn't make
     * the dialog visually jump.
     */
    verticalAnchor?: DialogVerticalAnchor;
    /**
     * Fill the viewport edge to edge. Content runs under the device's safe areas (notch, home
     * indicator) and insets itself. Size props and `preset` don't apply.
     */
    fullscreen?: boolean;
    /**
     * Set whether the dialog is internal.
     */
    internal?: boolean;
    /**
     * Set whether the dialog can be dismissed by clicking outside of it.
     * @default true
     */
    isDismissable?: boolean;
    /**
     * Pass the `state` object returned from `useDialogState`.
     */
    state: DialogTriggerState;
    /**
     * Defaults to the dialog itself rather than its first tabbable element, which would be the
     * header's close button, opening with its focus ring and tooltip showing.
     */
    initialFocus?: React.ComponentProps<typeof BaseDialog.Popup>['initialFocus'];
    finalFocus?: React.ComponentProps<typeof BaseDialog.Popup>['finalFocus'];
    /**
     * Manually set the dialog width.
     */
    width?: WidthTypes;
    /**
     * Manually set the dialog min width.
     */
    minWidth?: MinWidthTypes;
    /**
     * Manually set the dialog max width.
     */
    maxWidth?: MaxWidthTypes;
    /**
     * Manually set the dialog height.
     */
    height?: HeightTypes;
    /**
     * Manually set the dialog min height.
     */
    minHeight?: MinHeightTypes;
    /**
     * Manually set the dialog max height.
     */
    maxHeight?: MaxHeightTypes;
    /**
     * Whether to suppress the animations, designed for when navigating between two dialogs.
     */
    suppressAnimations?: boolean;
    /**
     * Called before closing via overlay dismissal; return false to cancel close.
     */
    onCloseAttempt?: () => boolean | void;
  };

export function Dialog({
  children,
  contentMode,
  preset,
  verticalAnchor = 'center',
  fullscreen = false,
  internal,
  width,
  minWidth,
  maxWidth,
  height,
  minHeight,
  maxHeight,
  state,
  initialFocus,
  finalFocus,
  isDismissable = true,
  suppressAnimations = false,
  onCloseAttempt,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  ...props
}: DialogProps) {
  // Extract heading from DialogHeader for accessibility
  const extractHeadingFromChildren = (dialogChildren: React.ReactNode): string | undefined => {
    if (!dialogChildren) return undefined;

    const childrenArray = React.Children.toArray(dialogChildren);
    for (const child of childrenArray) {
      if (React.isValidElement(child) && child.type === DialogHeader) {
        const headerProps = child.props as { heading?: React.ReactNode };
        if (headerProps.heading && typeof headerProps.heading === 'string') {
          return headerProps.heading;
        }
      }
    }
    return undefined;
  };

  const extractedHeading = extractHeadingFromChildren(children);
  const finalAriaLabel = ariaLabel || extractedHeading;
  let presetMaxWidth: MaxWidthTypes | undefined;
  if (preset === 'narrow') {
    presetMaxWidth = 400;
  } else if (preset === 'intermediate') {
    presetMaxWidth = 800;
  } else if (preset === 'wide') {
    presetMaxWidth = 1200;
  }

  // Opt-in override: pin the dialog's top edge so dynamic content doesn't
  // make the dialog visually bounce. We align the viewport to flex-start and
  // add top padding so the popup sits below the viewport edge rather than
  // vertically centered.
  const isTopPinned = verticalAnchor === 'topPinned';
  const viewportCSS = isTopPinned ? { alignItems: 'flex-start', paddingTop: '5vh' } : {};

  const css = fullscreen
    ? undefined
    : merge(
        widthCSS(width),
        minWidthCSS(minWidth),
        maxWidthCSS(preset ? presetMaxWidth : maxWidth),
        heightCSS(height),
        minHeightCSS(minHeight),
        maxHeightCSS(maxHeight),
      );

  const popupRef = React.useRef<HTMLDivElement>(null);

  // `state` is a fresh object literal every render, so key the memo on the stable `close` callback.
  const controlValue = React.useMemo(() => ({ onClose: state.close }), [state.close]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean, eventDetails: DialogRoot.ChangeEventDetails) => {
      if (nextOpen) {
        state.setOpen(true);
      } else if (onCloseAttempt && onCloseAttempt() === false) {
        eventDetails.cancel();
      } else {
        state.close();
      }
    },
    [onCloseAttempt, state],
  );

  const { onCloseComplete } = state;
  const handleOpenChangeComplete = React.useCallback(
    (open: boolean) => {
      if (!open) onCloseComplete?.();
    },
    [onCloseComplete],
  );

  return (
    <BaseDialog.Root
      open={state.isOpen}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={handleOpenChangeComplete}
      modal
      disablePointerDismissal={!isDismissable}
    >
      <BaseDialog.Portal>
        <ModalOverlay internal={internal} suppressAnimations={suppressAnimations} />
        <ModalViewport css={viewportCSS}>
          <ClearAccessLevelContext>
            <DialogControlContext.Provider value={controlValue}>
              <Modal
                ref={popupRef}
                initialFocus={initialFocus ?? popupRef}
                finalFocus={finalFocus}
                css={css}
                aria-label={finalAriaLabel}
                aria-labelledby={ariaLabelledby}
                fullscreen={fullscreen}
                suppressAnimations={suppressAnimations}
                {...props}
              >
                <DialogShell contentMode={contentMode} fullscreen={fullscreen} type="dialog">
                  {children}
                </DialogShell>
              </Modal>
            </DialogControlContext.Provider>
          </ClearAccessLevelContext>
        </ModalViewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  );
}
